import { ComponentType, ReactNode, lazy } from 'react';
import { matchPath } from 'react-router-dom';
import {
  UserOutlined,
  SettingOutlined,
  ClusterOutlined,
  DeploymentUnitOutlined,
  FileDoneOutlined,
  BookOutlined,
  AuditOutlined,
  WarningOutlined,
  MailOutlined,
  EyeOutlined,
  BuildOutlined,
  LaptopOutlined,
  RadarChartOutlined,
  CloudServerOutlined,
  StrikethroughOutlined,
  BorderOuterOutlined,
  UserSwitchOutlined,
  InfoCircleOutlined,
  ContactsOutlined,
  RetweetOutlined,
  BarChartOutlined,
  RocketOutlined,
  LineChartOutlined,
  TeamOutlined,
  RiseOutlined,
  TableOutlined,
  FileTextOutlined,
  DollarOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  BellOutlined,
  AlertOutlined,
} from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import ChidoriBlackIcon from 'images/chidori-black-logo.svg?react';
import NotebookIcon from 'images/notebook-logo.svg?react';
import IncortaOneIcon from 'images/incorta-one-icon.svg?react';
import { PermissionKey } from 'constants/permissionKeys';
import ClusterProvider from 'components/Clusters/contexts/ClusterContext';
import type { PageSkeletonKind } from 'components/PageSkeleton/PageSkeleton';
// Eagerly imported (not lazy): the extra chunk fetch was leaving the page stuck on
// its title while the bundle downloaded.
import CloudCapacityManagement from 'components/guidance/CloudCapacityManagement/CloudCapacityManagement';

const UserRoles = lazy(() => import('components/UserRoles/UserRoles'));
const OrganizationsPage = lazy(
  () => import('components/Organizations/OrganizationsPage'),
);
const ProvisionUsers = lazy(
  () => import('components/ProvisionUsers/ProvisionUsers'),
);
const Users = lazy(() => import('components/Users/Users'));
const Configurations = lazy(
  () => import('components/Configurations/Configurations'),
);
const AuditHistory = lazy(() => import('components/AuditHistory/AuditHistory'));
const InfraMap = lazy(() => import('components/InfraMap/InfraMap'));
const StaleResources = lazy(
  () => import('components/StaleResources/StaleResources'),
);
const Notification = lazy(() => import('components/Notification/Notification'));
const Backups = lazy(() => import('components/Backups/Backups'));
const Cli = lazy(() => import('components/Cli/Cli'));
const Clusters = lazy(() => import('components/Clusters/Clusters'));
const ContextManager = lazy(
  () => import('components/ContextManager/ContextManager'),
);
const DataAgents = lazy(() => import('components/DataAgents/DataAgents'));
const ExportedBlueprints = lazy(
  () => import('components/ExportedBlueprints/ExportedBlueprints'),
);
const FeatureBits = lazy(() => import('components/FeatureBits/FeatureBits'));
const Images = lazy(() => import('components/Images/images'));
const NewProvisionCustomer = lazy(
  () =>
    import('components/NewProvisionCustomer/NewProvisionCustomer/NewProvisionCustomer'),
);
const PlatformAction = lazy(
  () => import('components/PlatformAction/PlatformAction'),
);
const SparkImages = lazy(() => import('components/SparkImages/SparkImages'));
const PythonVersions = lazy(
  () => import('components/PythonVersions/PythonVersions'),
);
const SupportDashboard = lazy(
  () => import('components/SupportDashboard/SupportDashboard'),
);
const ToggleFeature = lazy(
  () => import('components/ToggleFeatures/ToggleFeature'),
);
const ToggleFeatures = lazy(
  () => import('components/ToggleFeatures/ToggleFeatures'),
);
const ContractsPage = lazy(() => import('components/Contracts/ContractsPage'));
const UserInfo = lazy(() => import('components/Users/UserInfo'));
const ClustersActions = lazy(
  () => import('components/clusterActions/ClusterActions'),
);
const ChidoriSizes = lazy(
  () => import('components/guidance/ChidoriSizes/ChidoriSizes'),
);
const NotebookUsersCount = lazy(
  () => import('components/Clusters/NotebookUsersCount/NotebookUsersCount'),
);
const Restore = lazy(() => import('components/Restore/Restore'));
const IncortaFlows = lazy(() => import('components/IncortaFlows/IncortaFlows'));
const Partners = lazy(() => import('components/Partners/Partners'));
const ScheduleScaling = lazy(
  () => import('components/Scaling/ScheduleScaling/ScheduleScaling'),
);
const ServicesScaling = lazy(
  () => import('components/Scaling/ServicesScaling/ServicesScaling'),
);
const ScalingMonitoring = lazy(
  () => import('components/Scaling/ScalingMonitoring/ScalingMonitoring'),
);
const Consumption = lazy(() => import('components/Consumption/Consumption'));
const ConsumptionGraph = lazy(
  () => import('components/ConsumptionGraph/ConsumptionGraph'),
);
const ConsumptionDashboard = lazy(
  () => import('components/ConsumptionDashboard/ConsumptionDashboard'),
);
const CmcSizes = lazy(() => import('components/guidance/CmcSizes/CmcSizes'));
const Deployments = lazy(() => import('components/Deployments/MainPage'));
const FinOps = lazy(() => import('components/FinOps/FinOps'));
const UsageAlerts = lazy(() => import('components/UsageAlerts/UsageAlerts'));
const AlertsAudit = lazy(
  () => import('components/UsageAlerts/AlertsAudit/AlertsAudit'),
);

function ClustersPage() {
  return (
    <ClusterProvider>
      <Clusters />
    </ClusterProvider>
  );
}

function UserDetailsPage() {
  return <UserInfo backToBtnText="Users" pushToURL="/users" />;
}

function ProvisionedUserDetailsPage() {
  return <UserInfo backToBtnText="Provisioning" pushToURL="/provision-user" />;
}

export type NavGroupKey =
  | 'users-management'
  | 'clusters-management'
  | 'system-management'
  | 'monitoring'
  | 'images-management'
  | 'scaling'
  | 'consumption'
  | 'guidance';

export const NAV_GROUPS: Record<NavGroupKey, { label: string; icon: ReactNode }> =
  {
    'users-management': { label: 'Users Management', icon: <UserOutlined /> },
    'clusters-management': {
      label: 'Clusters Management',
      icon: <ClusterOutlined />,
    },
    'system-management': {
      label: 'System Management',
      icon: <SettingOutlined />,
    },
    monitoring: { label: 'Monitoring', icon: <EyeOutlined /> },
    'images-management': { label: 'Images Management', icon: <BuildOutlined /> },
    scaling: { label: 'Scaling', icon: <DeploymentUnitOutlined /> },
    consumption: { label: 'Consumption', icon: <BarChartOutlined /> },
    guidance: { label: 'Sizing Management', icon: <InfoCircleOutlined /> },
  };

export interface AdminPage {
  /** Route pattern. */
  path: string;
  component: ComponentType;
  /** Any one of these keys opens the page. */
  requires: PermissionKey | PermissionKey[];
  /** Listed in the navigation only when it has a label. */
  label?: ReactNode;
  icon?: ReactNode;
  group?: NavGroupKey;
  /** Where the navigation item points, when the route is a pattern. */
  navPath?: string;
  className?: string;
  /** Sits beside the user menu, not in the main navigation. */
  account?: boolean;
  /** Takes the whole screen, outside the app's frame. */
  fullScreen?: boolean;
  skeleton?: PageSkeletonKind;
}

// The sub-page icons are white on the dark menu, so they size and fill themselves.
const smallNavIcon = { width: '12px', height: '12px', fill: 'white' };

/** Every permission-gated page, in navigation order. Routes and navigation are both built from it. */
export const ADMIN_PAGES: AdminPage[] = [
  {
    path: '/users',
    component: Users,
    requires: 'users.read',
    label: 'Users',
    icon: <UserOutlined />,
    group: 'users-management',
  },
  {
    path: '/userroles',
    component: UserRoles,
    requires: 'roles.read',
    label: <FormattedMessage id="userRolesPage.pageTitle" />,
    icon: <ContactsOutlined />,
    group: 'users-management',
  },
  {
    path: '/provision-user',
    component: ProvisionUsers,
    requires: 'provisioning.read',
    label: 'Provisioning',
    icon: <UserSwitchOutlined />,
    group: 'users-management',
  },
  {
    path: '/organizations',
    component: OrganizationsPage,
    requires: 'organizations.read',
    label: 'Organizations',
    icon: <TeamOutlined />,
    group: 'users-management',
  },
  {
    path: '/accounting/userdetails/:userid',
    component: UserDetailsPage,
    requires: 'users.read',
    skeleton: 'details',
  },
  {
    path: '/accounting/provisionuserdetails/:userid',
    component: ProvisionedUserDetailsPage,
    requires: 'provisioning.read',
    skeleton: 'details',
  },
  {
    path: '/provision-customer/new',
    component: NewProvisionCustomer,
    requires: 'provisioning.update',
    skeleton: 'form',
  },
  {
    path: '/exportedblueprints',
    component: ExportedBlueprints,
    requires: 'blueprints.read',
    label: 'Data Apps Management',
    icon: <FileDoneOutlined />,
  },
  {
    path: '/incorta-flows',
    component: IncortaFlows,
    requires: 'flows.read',
    label: 'Workflow Management',
    icon: <RetweetOutlined />,
  },
  // Splat, not two routes: the list and a cluster's details share this
  // element so navigating between them never remounts the page.
  {
    path: '/clusters/*',
    navPath: '/clusters',
    component: ClustersPage,
    requires: 'clusters.read',
    label: 'Clusters',
    icon: <ClusterOutlined />,
    group: 'clusters-management',
  },
  {
    path: '/backups',
    component: Backups,
    requires: 'backups.read',
    label: 'Backups',
    icon: <CloudServerOutlined />,
    group: 'clusters-management',
  },
  {
    path: '/restore',
    component: Restore,
    requires: 'restores.read',
    label: 'Restored Clusters',
    icon: <RetweetOutlined />,
    group: 'clusters-management',
  },
  {
    path: '/configurations',
    component: Configurations,
    requires: 'configurations.read',
    label: 'Configurations',
    icon: <SettingOutlined />,
    group: 'system-management',
  },
  {
    path: '/platform',
    component: PlatformAction,
    requires: 'platformActions.read',
    label: 'Platform Actions',
    icon: <BorderOuterOutlined />,
    group: 'system-management',
  },
  {
    path: '/featurebits',
    component: FeatureBits,
    requires: 'featureBits.read',
    label: 'Feature Bits',
    icon: <LaptopOutlined />,
    group: 'system-management',
  },
  {
    path: '/togglefeatures',
    component: ToggleFeatures,
    requires: 'toggleFeatures.read',
    label: 'Toggle Features',
    icon: <DeploymentUnitOutlined />,
    group: 'system-management',
  },
  {
    path: '/togglefeatures/:feature',
    component: ToggleFeature,
    requires: 'toggleFeatures.read',
    skeleton: 'details',
  },
  {
    path: '/contracts',
    component: ContractsPage,
    requires: 'users.viewPackages',
    label: 'Contracts',
    icon: <FileTextOutlined />,
    group: 'system-management',
  },
  {
    path: '/clicommands',
    component: Cli,
    requires: 'cli.read',
    label: 'CLI Commands',
    icon: <BookOutlined />,
    group: 'system-management',
  },
  {
    path: '/contextmanager',
    component: ContextManager,
    requires: 'operators.read',
    label: 'Operators',
    icon: <RadarChartOutlined />,
    group: 'system-management',
  },
  {
    path: '/usagealerts',
    component: UsageAlerts,
    requires: 'consumption.read',
    label: 'Usage Alerts',
    icon: <BellOutlined />,
    group: 'system-management',
  },
  {
    path: '/audit',
    component: AuditHistory,
    requires: 'audit.read',
    label: 'Audit History',
    icon: <AuditOutlined />,
    group: 'monitoring',
  },
  {
    path: '/stale-resources',
    component: StaleResources,
    requires: 'audit.read',
    label: 'Stale Resources',
    icon: <WarningOutlined />,
    group: 'monitoring',
    skeleton: 'dashboard',
  },
  {
    path: '/notifications',
    component: Notification,
    requires: 'notifications.read',
    label: 'Notifications',
    icon: <MailOutlined />,
    group: 'monitoring',
  },
  {
    path: '/alertsaudit',
    component: AlertsAudit,
    requires: 'consumption.read',
    label: 'Alerts Audit',
    icon: <AlertOutlined />,
    group: 'monitoring',
  },
  {
    path: '/clustersactions',
    component: ClustersActions,
    requires: 'actionCenter.read',
  },
  {
    path: '/images',
    component: Images,
    requires: 'images.read',
    label: 'Incorta Images',
    icon: <BuildOutlined />,
    group: 'images-management',
  },
  {
    path: '/sparkimages',
    component: SparkImages,
    requires: 'images.read',
    label: 'Spark Images',
    icon: <StrikethroughOutlined />,
    group: 'images-management',
  },
  {
    path: '/python-versions',
    component: PythonVersions,
    requires: 'images.read',
    label: 'Python Versions',
    icon: <BuildOutlined />,
    group: 'images-management',
  },
  {
    path: '/schedule-scaling',
    component: ScheduleScaling,
    requires: 'scheduledScaling.read',
    label: 'Schedule Scaling',
    icon: <DeploymentUnitOutlined />,
    group: 'scaling',
  },
  {
    path: '/services-scaling',
    component: ServicesScaling,
    requires: 'serviceScaling.read',
    label: 'Services Scaling',
    icon: <RiseOutlined />,
    group: 'scaling',
  },
  {
    path: '/scaling-monitoring',
    component: ScalingMonitoring,
    requires: 'serviceScaling.read',
    label: 'Scaling Monitoring',
    icon: <LineChartOutlined />,
    group: 'scaling',
    skeleton: 'dashboard',
  },
  {
    path: '/consumption/consumption-records',
    component: Consumption,
    requires: 'consumption.read',
    label: 'Consumption Records',
    icon: <TableOutlined />,
    group: 'consumption',
  },
  {
    path: '/consumption/analytics-and-charts',
    component: ConsumptionGraph,
    requires: 'consumption.read',
    label: 'Analytics & Charts',
    icon: <LineChartOutlined />,
    group: 'consumption',
    skeleton: 'dashboard',
  },
  {
    path: '/consumption/dashboards',
    component: ConsumptionDashboard,
    requires: 'dashboards.read',
    label: 'Dashboards',
    icon: <DashboardOutlined />,
    group: 'consumption',
    skeleton: 'dashboard',
  },
  // One route for every FinOps tab so switching tabs never remounts it; FinOps gates the GCP billing
  // tab on billing.read and the others on finops.read.
  {
    path: '/billing/finops/*',
    navPath: '/billing/finops',
    component: FinOps,
    requires: ['finops.read', 'billing.read'],
    label: 'FinOps',
    icon: <DollarOutlined />,
    skeleton: 'dashboard',
  },
  {
    path: '/capacity-management',
    component: CloudCapacityManagement,
    requires: 'sizes.read',
    label: 'Cloud Capacity Management',
    icon: <ClusterOutlined />,
    group: 'guidance',
    skeleton: 'form',
  },
  {
    path: '/cmc-sizes',
    component: CmcSizes,
    requires: 'cmcSizes.read',
    label: 'CMC Sizes',
    icon: <CloudServerOutlined />,
    group: 'guidance',
  },
  {
    path: '/chidori-sizes',
    component: ChidoriSizes,
    requires: 'chidori.read',
    label: 'Chidori Sizes',
    icon: <ChidoriBlackIcon style={smallNavIcon} />,
    group: 'guidance',
    className: 'chidori-sizes-nav',
  },
  {
    path: '/notebook-sizes',
    component: NotebookUsersCount,
    requires: 'clusters.read',
    label: 'Notebook Users Count',
    icon: <NotebookIcon style={smallNavIcon} />,
    group: 'guidance',
    className: 'notebook-sizes-nav',
  },
  {
    path: '/deployments',
    component: Deployments,
    requires: 'deployments.read',
    label: 'Deployments Management',
    icon: <RocketOutlined style={smallNavIcon} />,
    className: 'deployments-nav',
  },
  {
    path: '/incorta-one',
    component: Partners,
    requires: 'incortaOne.read',
    label: 'IncortaOne',
    icon: <IncortaOneIcon style={{ width: '16px', height: '16px' }} />,
  },
  {
    path: '/dataagents',
    component: DataAgents,
    requires: 'dataAgents.read',
  },
  {
    path: '/support-dashboard',
    component: SupportDashboard,
    requires: 'supportDashboard.read',
    label: 'Support Dashboard',
    icon: <CustomerServiceOutlined />,
    account: true,
  },
  {
    path: '/clusters/:clusterName/infrastructure',
    component: InfraMap,
    requires: 'infrastructure.read',
    fullScreen: true,
    skeleton: 'canvas',
  },
];

export const navPathOf = (page: AdminPage) => page.navPath ?? page.path;

export const skeletonAt = (pathname: string): PageSkeletonKind =>
  pathname === '/'
    ? 'dashboard'
    : ADMIN_PAGES.find(page => matchPath(page.path, pathname))?.skeleton ??
      'table';

export const canOpen = (
  page: AdminPage,
  canAny: (...keys: PermissionKey[]) => boolean,
) => canAny(...[page.requires].flat());

/** Where to land when Home has nothing to show: the first page the admin may open without a parameter. */
export function firstOpenablePath(
  canAny: (...keys: PermissionKey[]) => boolean,
) {
  const page = ADMIN_PAGES.find(
    candidate => !candidate.path.includes(':') && canOpen(candidate, canAny),
  );
  return page && navPathOf(page);
}
